import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Users, GraduationCap, School as SchoolIcon, Coins, ClipboardCheck,
  Calendar, MessageSquare, FileSpreadsheet, Award, Sparkles, BarChart3, Bus,
  Bell, Search, Phone, Mail, UserCircle2, IdCard, Clock, ChevronLeft,
  Briefcase, Megaphone, ListChecks, Wallet,
} from "lucide-react";
import DailyNotesCard from "../components/DailyNotesCard";
import { SchoolSelect, useSchools } from "../components/SchoolSelect";
import { localName, localFullName } from "../lib/localName";

const Stat = ({ icon: Icon, label, value, accent = "primary", testId }) => (
  <Card className="p-4 sm:p-5 border border-border/60 rounded-2xl shadow-sm touch-card bg-card" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-bold truncate">{label}</div>
        <div className="text-2xl sm:text-3xl font-bold mt-1.5 sm:mt-2 tabular-nums">{value}</div>
      </div>
      <div className={`shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl grid place-items-center bg-${accent}/10 text-${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

const QuickLink = ({ to, icon: Icon, label, accent = "primary", testId }) => ( // eslint-disable-line no-unused-vars
  <Link to={to} className="block" data-testid={testId}>
    <Card className="p-5 border border-border shadow-none card-hover smooth h-full">
      <Icon className={`w-6 h-6 text-${accent} mb-3`} />
      <div className="font-medium">{label}</div>
    </Card>
  </Link>
);
const ActionCard = ({ to, icon: Icon, title, desc, cta, testId }) => (
  <Card className="overflow-hidden border-0 shadow-md hover:shadow-xl card-hover smooth h-full flex flex-col" data-testid={testId}>
    <div className="bg-gradient-to-br from-primary to-primary/85 text-primary-foreground py-10 flex items-center justify-center relative">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,255,255,.3), transparent 50%)" }} />
      <Icon className="w-12 h-12 relative" strokeWidth={1.5} />
    </div>
    <div className="bg-card p-6 flex flex-col flex-1">
      <h3 className="text-xl font-bold text-primary mb-3 text-center">{title}</h3>
      <p className="text-sm text-muted-foreground text-center leading-relaxed flex-1">{desc}</p>
      <Link to={to} className="mt-5">
        <Button className="w-full" data-testid={`${testId}-cta`}>{cta}</Button>
      </Link>
    </div>
  </Card>
);

// ========== Shared Hero + Info Bar (used by all roles) ==========
function DashboardHero({ user, title, subtitle, q, setQ, badgeCount }) {
  const {  t , lang } = useI18n();
  return (
    <Card className="overflow-hidden border-0 shadow-lg" data-testid="dashboard-hero">
      <div className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6 sm:p-8">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 0%, rgba(255,255,255,.4), transparent 40%)" }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <Link to="/profile" className="flex items-start gap-4 hover:opacity-90 smooth" data-testid="hero-profile-link">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
              <UserCircle2 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{title}</h1>
              <p className="text-sm text-primary-foreground/80 mt-1 leading-relaxed">{subtitle}</p>
            </div>
          </Link>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {setQ && (
              <div className="relative flex-1 sm:w-72">
                <Search className="absolute top-3 start-3 w-4 h-4 text-primary-foreground/60" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder={t("search_dashboard")} className="ps-10 bg-white/95 text-foreground border-0 shadow-md focus-visible:ring-2 focus-visible:ring-white/40 h-10" data-testid="dashboard-search" />
              </div>
            )}
            <Link to="/notifications" className="relative w-10 h-10 rounded-full bg-white/15 backdrop-blur grid place-items-center hover:bg-white/25 smooth" data-testid="hero-notif">
              <Bell className="w-5 h-5" />
              {badgeCount > 0 && (
                <span className="absolute -top-1 -end-1 min-w-[20px] h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold grid place-items-center">{Math.min(badgeCount, 9)}</span>
              )}
            </Link>
          </div>
        </div>
      </div>
    </Card>
  );
}

function UserInfoBar({ user }) {
  const {  t , lang } = useI18n();
  const lastLogin = user.last_login || user.created_at || "";
  return (
    <Link to="/profile" data-testid="user-info-bar" className="block">
      <Card className="border shadow-none p-4 sm:p-5 card-hover smooth hover:border-primary/40 cursor-pointer">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
          <InfoCell icon={UserCircle2} label={t("name")} value={user.name || "—"} />
          <InfoCell icon={IdCard} label={t("role")} value={t(user.role) || user.role} />
          <InfoCell icon={Mail} label={t("email")} value={user.email} mono />
          <InfoCell icon={Phone} label={t("phone")} value={user.phone || "—"} mono />
          <InfoCell icon={Clock} label={t("last_login")} value={lastLogin ? new Date(lastLogin).toLocaleString("ar-SA", { hour: "2-digit", minute: "2-digit" }) : "—"} highlight />
        </div>
        <div className="text-xs text-primary mt-3 flex items-center justify-end gap-1 opacity-80">
          <UserCircle2 className="w-3.5 h-3.5" />
          <span>{t("click_to_edit_profile2")}</span>
        </div>
      </Card>
    </Link>
  );
}

// ========== Admin / Staff Dashboard ==========
function AdminDashboard() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const { schools, schoolId, setSchoolId, isSuper, queryId } = useSchools({ allowAll: true });
  const [stats, setStats] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    const qs = queryId ? `?school_id=${queryId}` : "";
    api.get(`/dashboard/stats${qs}`).then(r => setStats(r.data)).catch(() => {});
    api.get("/announcements").then(r => setAnnouncements(r.data || [])).catch(() => {});
  }, [queryId]);

  // Main action cards (driven by user role)
  const allCards = [
    { to: "/students", icon: GraduationCap, title: t("students"), desc: t("desc_students"), cta: t("students"), key: "students", roles: ["super_admin", "school_admin", "academic"] },
    { to: "/teachers", icon: Users, title: t("teachers"), desc: t("desc_teachers"), cta: t("teachers"), key: "teachers", roles: ["super_admin", "school_admin", "academic"] },
    { to: "/attendance", icon: ClipboardCheck, title: t("attendance"), desc: t("desc_attendance"), cta: t("attendance"), key: "attendance", roles: ["super_admin", "school_admin", "academic", "teacher"] },
    { to: "/classes", icon: SchoolIcon, title: t("classes"), desc: t("desc_classes"), cta: t("classes"), key: "classes", roles: ["super_admin", "school_admin", "academic"] },
    { to: "/subjects", icon: ListChecks, title: t("subjects"), desc: t("desc_subjects"), cta: t("subjects"), key: "subjects", roles: ["super_admin", "school_admin", "academic"] },
    { to: "/timetable", icon: Calendar, title: t("timetable"), desc: t("desc_timetable"), cta: t("timetable"), key: "timetable", roles: ["super_admin", "school_admin", "academic"] },
    { to: "/hr/employees", icon: Briefcase, title: t("hr"), desc: t("desc_hr"), cta: t("hr"), key: "hr", roles: ["super_admin", "school_admin", "hr", "finance"] },
    { to: "/transport/vehicles", icon: Bus, title: t("transport"), desc: t("desc_transport"), cta: t("transport"), key: "transport", roles: ["super_admin", "school_admin", "academic"] },
    { to: "/fees", icon: Coins, title: t("fees"), desc: t("desc_fees"), cta: t("fees"), key: "fees", roles: ["super_admin", "school_admin", "finance"] },
    { to: "/announcements", icon: Megaphone, title: t("announcements"), desc: t("desc_announcements"), cta: t("announcements"), key: "announcements", roles: ["super_admin", "school_admin", "academic", "teacher"] },
    { to: "/ai", icon: Sparkles, title: t("ai_assistant"), desc: t("desc_ai"), cta: t("ai_assistant"), key: "ai", roles: ["super_admin", "school_admin", "academic", "teacher"] },
    { to: "/reports", icon: BarChart3, title: t("reports"), desc: t("desc_reports"), cta: t("reports"), key: "reports", roles: ["super_admin", "school_admin", "academic", "finance"] },
  ];
  const cards = allCards.filter(c => c.roles.includes(user.role));
  const filteredCards = q ? cards.filter(c => (c.title || "").includes(q) || (c.desc || "").includes(q)) : cards;

  return (
    <div className="space-y-6">
      <DashboardHero user={user} title={t("hero_title_admin")} subtitle={`${t("hero_subtitle")} — ${user.name || user.email}`} q={q} setQ={setQ} badgeCount={announcements.length} />
      <UserInfoBar user={user} />

      {/* School scope selector */}
      {(isSuper || schools.length > 0) && (
        <Card className="p-4 border shadow-none flex flex-wrap items-center gap-3">
          <SchoolIcon className="w-4 h-4 text-primary shrink-0" />
          <span className="text-sm font-medium text-muted-foreground">{t("select_school")}</span>
          <SchoolSelect
            schools={schools}
            value={schoolId}
            onChange={setSchoolId}
            testId="dashboard-school-select"
            disabled={!isSuper}
            showAll={isSuper}
          />
        </Card>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Stat testId="stat-students" icon={GraduationCap} label={t("students_count")} value={stats.students ?? 0} />
        <Stat testId="stat-teachers" icon={Users} label={t("teachers_count")} value={stats.teachers ?? 0} accent="secondary" />
        <Stat testId="stat-classes" icon={SchoolIcon} label={t("classes_count")} value={stats.classes ?? 0} accent="accent" />
        <Stat testId="stat-attendance" icon={ClipboardCheck} label={t("today_attendance")} value={stats.today_attendance_records ?? 0} />
        <Stat testId="stat-invoices" icon={Coins} label={t("open_invoices")} value={stats.invoices_open ?? 0} accent="secondary" />
      </div>

      {/* Action cards grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredCards.map(c => (
          <ActionCard key={c.key} to={c.to} icon={c.icon} title={c.title} desc={c.desc} cta={c.cta} testId={`action-${c.key}`} />
        ))}
        {filteredCards.length === 0 && (
          <Card className="col-span-full p-12 text-center text-muted-foreground border-dashed">{t("no_data")}</Card>
        )}
      </div>

      {/* Recent announcements */}
      {announcements.length > 0 && (
        <Card className="p-6 border shadow-none">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold flex items-center gap-2"><Megaphone className="w-5 h-5 text-primary" /> {t("announcements")}</h3>
            <Link to="/announcements" className="text-sm text-primary hover:underline">{t("view_all")} <ChevronLeft className="w-3 h-3 inline lucide-rotate-rtl" /></Link>
          </div>
          <ul className="divide-y divide-border">
            {announcements.slice(0, 5).map(a => (
              <li key={a.id} className="py-3" data-testid={`announcement-${a.id}`}>
                <div className="font-medium">{a.title}</div>
                <div className="text-sm text-muted-foreground line-clamp-2">{a.body}</div>
                <div className="text-xs text-muted-foreground/80 mt-1 font-mono">{a.at?.slice(0, 10)}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function InfoCell({ icon: Icon, label, value, mono = false, highlight = false }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${highlight ? "bg-primary/8" : "bg-muted/40"}`}>
      <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${highlight ? "bg-primary/15 text-primary" : "bg-card text-muted-foreground"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
        <div className={`font-medium truncate ${mono ? "font-mono text-xs" : "text-sm"}`}>{value}</div>
      </div>
    </div>
  );
}

// ========== Teacher Dashboard ==========
function TeacherDashboard() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ classes: [], slots: [], teacher: null });
  const [today, setToday] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get("/my/teacher-classes").then(r => setData(r.data || { classes: [], slots: [] })).catch(() => {});
    api.get("/announcements").then(r => setAnnouncements(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (data.slots) {
      const dayIdx = new Date().getDay();
      setToday(data.slots.filter(s => s.day === dayIdx).sort((a, b) => a.period - b.period));
    }
  }, [data]);

  const cards = [
    { to: "/attendance", icon: ClipboardCheck, title: t("rollCall"), desc: t("desc_attendance"), cta: t("rollCall"), key: "att" },
    { to: "/grades-entry", icon: Award, title: t("gradesEntry"), desc: t("desc_my_grades"), cta: t("gradesEntry"), key: "grades" },
    { to: "/homework", icon: FileSpreadsheet, title: t("homework"), desc: t("desc_my_homework"), cta: t("homework"), key: "hw" },
    { to: "/timetable", icon: Calendar, title: t("my_schedule"), desc: t("desc_my_schedule"), cta: t("my_schedule"), key: "sched" },
    { to: "/ai", icon: Sparkles, title: t("ai_assistant"), desc: t("desc_ai"), cta: t("ai_assistant"), key: "ai" },
    { to: "/messages", icon: MessageSquare, title: t("messages"), desc: t("desc_messages"), cta: t("messages"), key: "msg" },
  ];

  return (
    <div className="space-y-6">
      <DashboardHero user={user} title={t("hero_title_teacher")} subtitle={`${t("hero_subtitle")} — ${user.name || user.email}`} badgeCount={announcements.length} />
      <UserInfoBar user={user} />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat icon={SchoolIcon} label={t("my_classes")} value={data.classes?.length || 0} testId="t-classes" />
        <Stat icon={Calendar} label={t("today_attendance")} value={today.length} accent="secondary" testId="t-today" />
        <Stat icon={ClipboardCheck} label={t("attendance")} value="—" accent="accent" testId="t-att" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-6 border shadow-none">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" />{t("my_schedule")} · {t("day")}</h3>
          {today.length === 0
            ? <div className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</div>
            : <ul className="divide-y divide-border">
                {today.map(s => (
                  <li key={s.id} className="py-3 flex justify-between items-center">
                    <div>
                      <div className="font-medium">{t("period")} {s.period}</div>
                      <div className="text-xs text-muted-foreground font-mono">{s.start_time} — {s.end_time}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{s.room}</div>
                  </li>
                ))}
              </ul>}
        </Card>
        <Card className="p-6 border shadow-none">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><SchoolIcon className="w-5 h-5 text-primary" />{t("my_classes")}</h3>
          {data.classes?.length === 0
            ? <div className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</div>
            : <ul className="space-y-2">
                {(data.classes || []).map(c => (
                  <li key={c.id}>
                    <Link to="/attendance" className="block p-3 rounded-lg border border-border hover:bg-muted smooth">
                      <span className="font-medium">{c.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>}
        </Card>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map(c => (
          <ActionCard key={c.key} to={c.to} icon={c.icon} title={c.title} desc={c.desc} cta={c.cta} testId={`t-action-${c.key}`} />
        ))}
      </div>
    </div>
  );
}

// ========== Parent Dashboard ==========
function ParentDashboard() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [childTrips, setChildTrips] = useState({});
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get("/my/children").then(r => {
      const kids = r.data || [];
      setChildren(kids);
      const today = new Date().toISOString().slice(0, 10);
      kids.forEach(c => {
        api.get(`/trips?student_id=${c.id}&date=${today}`)
          .then(tr => setChildTrips(prev => ({ ...prev, [c.id]: tr.data || [] })))
          .catch(() => {});
      });
    });
    api.get("/invoices").then(r => setInvoices(r.data || []));
    api.get("/announcements").then(r => setAnnouncements(r.data || [])).catch(() => {});
  }, []);

  const open = invoices.filter(i => i.status !== "paid");

  const cards = [
    { to: "/attendance", icon: ClipboardCheck, title: t("attendance"), desc: t("desc_attendance"), cta: t("attendance"), key: "att" },
    { to: "/report-card", icon: Award, title: t("reportCard"), desc: t("desc_my_grades"), cta: t("reportCard"), key: "rc" },
    { to: "/fees", icon: Coins, title: t("fees"), desc: t("desc_fees"), cta: t("fees"), key: "fees" },
    { to: "/homework", icon: FileSpreadsheet, title: t("homework"), desc: t("desc_my_homework"), cta: t("homework"), key: "hw" },
    { to: "/messages", icon: MessageSquare, title: t("messages"), desc: t("desc_messages"), cta: t("messages"), key: "msg" },
    { to: "/timetable", icon: Calendar, title: t("my_schedule"), desc: t("desc_my_schedule"), cta: t("my_schedule"), key: "sched" },
  ];

  return (
    <div className="space-y-6">
      <DashboardHero user={user} title={t("hero_title_parent")} subtitle={`${t("hero_subtitle")} — ${user.name || user.email}`} badgeCount={announcements.length} />
      <UserInfoBar user={user} />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat icon={GraduationCap} label={t("my_children")} value={children.length} testId="p-children" />
        <Stat icon={Coins} label={t("fees_due")} value={open.length} accent="accent" testId="p-fees-due" />
        <Stat icon={MessageSquare} label={t("messages")} value="—" accent="secondary" testId="p-msgs" />
      </div>

      {/* AI-generated daily notes (only shows when school has it enabled) */}
      <DailyNotesCard />

      <Card className="p-6 border shadow-none">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-primary" />{t("my_children")}</h3>
        {children.length === 0
          ? <div className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</div>
          : <div className="grid md:grid-cols-2 gap-4">
              {children.map(c => {
                const trips = childTrips[c.id] || [];
                const morning = trips.find(t_ => t_.direction === "morning");
                const evening = trips.find(t_ => t_.direction === "evening");
                return (
                  <Card
                    key={c.id}
                    className="p-4 border shadow-none card-hover smooth cursor-pointer hover:border-primary/40 hover:shadow-md transition"
                    data-testid={`p-child-${c.id}`}
                    onClick={() => navigate(`/students/${c.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center"><GraduationCap className="w-5 h-5"/></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{localFullName(c, lang)}</div>
                        <div className="text-xs text-muted-foreground font-mono">{c.national_id || "—"}</div>
                      </div>
                      <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition">{t("view_full_profile")} ←</span>
                    </div>
                    {(morning || evening) && (
                      <div className="mt-3 border-t border-border pt-3 space-y-1.5">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{t("transport")}</div>
                        {morning && <TripBadge t={t} trip={morning} childId={c.id} />}
                        {evening && <TripBadge t={t} trip={evening} childId={c.id} />}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>}
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map(c => (
          <ActionCard key={c.key} to={c.to} icon={c.icon} title={c.title} desc={c.desc} cta={c.cta} testId={`p-action-${c.key}`} />
        ))}
      </div>
    </div>
  );
}

function TripBadge({ t, trip, childId }) {
  const color = trip.status === "completed" ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
    : trip.status === "started" ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20"
    : "text-muted-foreground bg-muted";
  return (
    <Link to={`/parent/trip/${trip.id}`} className="flex items-center justify-between gap-2 text-xs hover:underline" data-testid={`p-trip-${childId}-${trip.direction}`}>
      <span className="flex items-center gap-1"><Bus className="w-3.5 h-3.5" /> {t(trip.direction)}</span>
      <span className={`px-2 py-0.5 rounded-full font-medium ${color}`}>{t(trip.status)}{trip.delay_min ? ` (+${trip.delay_min}m)` : ""}</span>
    </Link>
  );
}

// ========== Student Dashboard ==========
function StudentDashboard() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get("/my/student-record").then(r => setMe(r.data)).catch(() => {});
    api.get("/announcements").then(r => setAnnouncements(r.data || [])).catch(() => {});
  }, []);

  const cards = [
    { to: "/canteen/my-wallet", icon: Wallet, title: t("my_wallet"), desc: t("student_qr_hint"), cta: t("canteen_my_wallet"), key: "wallet" },
    { to: "/timetable", icon: Calendar, title: t("my_schedule"), desc: t("desc_my_schedule"), cta: t("my_schedule"), key: "sched" },
    { to: "/report-card", icon: Award, title: t("my_grades"), desc: t("desc_my_grades"), cta: t("my_grades"), key: "grades" },
    { to: "/homework", icon: FileSpreadsheet, title: t("my_homework"), desc: t("desc_my_homework"), cta: t("my_homework"), key: "hw" },
    { to: "/attendance", icon: ClipboardCheck, title: t("attendance"), desc: t("desc_attendance"), cta: t("attendance"), key: "att" },
    { to: "/announcements", icon: Megaphone, title: t("announcements"), desc: t("desc_announcements"), cta: t("announcements"), key: "ann" },
  ];

  return (
    <div className="space-y-6">
      <DashboardHero user={user} title={t("hero_title_student")} subtitle={`${t("hero_subtitle")} — ${localFullName(me, lang) || user.name || user.email}`} badgeCount={announcements.length} />
      <UserInfoBar user={user} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map(c => (
          <ActionCard key={c.key} to={c.to} icon={c.icon} title={c.title} desc={c.desc} cta={c.cta} testId={`s-action-${c.key}`} />
        ))}
      </div>
    </div>
  );
}

// ========== Main Dispatcher ==========
export default function DashboardPage() {
  const { user } = useAuth();

  // Drivers and HR have dedicated landing pages
  if (user?.role === "driver") return <Navigate to="/my-route" replace />;
  if (user?.role === "hr") return <Navigate to="/hr" replace />;
  // Canteen staff have only POS access — redirect immediately
  if (user?.role === "canteen_staff") return <Navigate to="/canteen/pos" replace />;

  if (user.role === "teacher") return <TeacherDashboard />;
  if (user.role === "parent") return <ParentDashboard />;
  if (user.role === "student") return <StudentDashboard />;
  return <AdminDashboard />;
}
